import {
  AnimatePresence,
  motion,
  Reorder,
  useDragControls,
} from 'motion/react';
import type { DragControls } from 'motion/react';
import { Control, useFormState, UseFormSetValue } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertCircle,
  CheckCircle2,
  GripVertical,
  Loader2,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { cx } from 'class-variance-authority';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  ONBOARDING_ICON_CATALOG,
  OnboardingIconCatalogEntry,
} from '@/lib/onboarding-icon-catalog';
import {
  checkTabLink,
  INTERNAL_TAB_PATHS,
  maskTabLink,
  TabBarConfigFormValues,
  TabConfigFormValues,
} from '@/pages/onboarding/4-tab-bar-config.schema';

interface DraggableTabRowProps extends Omit<TabRowProps, 'dragControls'> {
  tab: TabConfigFormValues;
}

// Wraps TabRow in a `Reorder.Item`, giving reordering its "Apple-style"
// feel: each item's position is a real layout-animated value (Framer
// Motion's `layout`), so dropping a row springs every other row smoothly
// into its new slot instead of snapping. A dedicated component (rather than
// inlining this in the list's `.map`) because `useDragControls` is a hook —
// it can't be called directly inside a loop body.
export function DraggableTabRow({ tab, ...tabRowProps }: DraggableTabRowProps) {
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      as="div"
      value={tab}
      dragControls={dragControls}
      // The grip handle (see TabRow) starts the drag by hand instead —
      // without this, the whole row (including its text inputs) would
      // itself be draggable, which eats the clicks/selection those inputs
      // need.
      dragListener={false}
      // Reorder already reorders the *data* live as you drag past another
      // row (that's what makes the list push out of the way) — without a
      // visual "lift", the row actually under your cursor and the row that
      // just slid into its old spot look identical mid-drag, which reads as
      // the drag having jumped to that other row instead of what it really
      // is: your row still following the cursor, everything else just
      // reflowing around it.
      whileDrag={{
        scale: 1.02,
        boxShadow: '0 8px 20px -6px rgb(0 0 0 / 0.25)',
      }}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      // No `exit` — a removed row's own inputs are kept mounted (and
      // registered) for as long as an exit transition plays, and that was
      // observed to let its still-live, register()-bound DOM values get
      // written back into the array after the removal already shrank it,
      // resurrecting it as a key/iconKey-less ghost. Dropping `exit` makes
      // AnimatePresence a no-op for removal — the row simply disappears
      // immediately, which also means it no longer needs AnimatePresence as
      // a wrapper.
      transition={{
        duration: 0.18,
        layout: { type: 'spring', bounce: 0.2, duration: 0.4 },
      }}
    >
      <TabRow {...tabRowProps} tab={tab} dragControls={dragControls} />
    </Reorder.Item>
  );
}

export interface TabRowProps {
  /** This row's own data — always the current value for its `key`. */
  tab: TabConfigFormValues;
  control: Control<TabBarConfigFormValues>;
  /** Position in the form array; only used to address writes and errors. */
  index: number;
  setValue: UseFormSetValue<TabBarConfigFormValues>;
  onRemove: () => void;
  /** Called when any field in this row gains focus — see `activeTabKey`. */
  onActivate: () => void;
  /** Attaches the row's root element — lets the phone mockup scroll to it. */
  rowRef: (el: HTMLDivElement | null) => void;
  /** Lifted up so tapping this tab in the phone mockup can open it directly. */
  iconPickerOpen: boolean;
  onIconPickerOpenChange: (open: boolean) => void;
  /** Starts the row's drag — see DraggableTabRow's `dragListener={false}`. */
  dragControls: DragControls;
}

// One compact, always-editable row per tab — no expand/collapse, so the
// list's height stays predictable (at most MAX_TABS rows) instead of
// ballooning the page whenever a row is opened to edit. Each row renders from
// its own `tab` prop and only subscribes to its own slice of the form's
// errors (scoped useFormState).
export function TabRow({
  tab,
  control,
  index,
  setValue,
  onRemove,
  onActivate,
  rowRef,
  iconPickerOpen,
  onIconPickerOpenChange,
  dragControls,
}: TabRowProps) {
  // Read from the `tab` prop, never via `useWatch`/`register`/`Controller`
  // on `tabs.${index}.*`: those subscribe by field *name*, and when a row is
  // dragged to a new position its `index` (and so its name) changes in the
  // same update that rewrites the array. The subscription fires first, still
  // under the old name, so a row would pick up the value of whichever tab
  // used to sit at its old index — swapped icons, and a custom tab briefly
  // treated as a preset (or vice versa). The row's `key` is stable and its
  // parent re-renders with the current array, so the prop is always right;
  // `index` is only needed to say where to write.
  const { path, iconKey, title } = tab;
  const { errors } = useFormState({ control, name: `tabs.${index}` });

  const isInternalTab = INTERNAL_TAB_PATHS.has(path);
  const selectedIcon: OnboardingIconCatalogEntry | undefined =
    ONBOARDING_ICON_CATALOG.find((entry) => entry.key === iconKey);

  // Surfaced separately per field (not merged into one message) — a custom
  // tab is created with both title and path empty, so fixing the link alone
  // used to look like it "didn't work": the row's one shared error slot
  // kept showing the title's "Campo obrigatório" the whole time, since that
  // field also still had its own, unrelated error.
  const rowErrors = errors.tabs?.[index];
  const titleErrorMessage = rowErrors?.title?.message;
  const pathErrorMessage = rowErrors?.path?.message;
  const iconKeyErrorMessage = rowErrors?.iconKey?.message;
  const hasRowError = !!(
    titleErrorMessage ||
    pathErrorMessage ||
    iconKeyErrorMessage
  );

  // Live, as-you-type feedback on the link — same pattern as the store link
  // field on the previous step (AppNameAndLinkPage's checkStoreUrl): debounce
  // first so the "checking" state gets a beat to show, then settle on
  // valid/invalid. Only relevant for custom tabs — preset tabs never show a
  // link field at all.
  const debouncedPath = useDebouncedValue(path, 300);
  const isCheckingLink =
    !isInternalTab && path !== debouncedPath && path.length > 0;
  const linkCheck = checkTabLink(debouncedPath ?? '');

  return (
    <div
      ref={rowRef}
      onFocus={onActivate}
      className={cx(
        'flex items-center gap-2 rounded-xl border px-2 py-1.5 transition-shadow',
        'focus-within:ring-2 focus-within:ring-primary/30',
        hasRowError
          ? 'border-red-300 bg-red-50/60'
          : isInternalTab
            ? 'border-primary/20 bg-primary/5'
            : 'border-border'
      )}
    >
      <button
        type="button"
        aria-label="Arrastar para reordenar"
        className="flex h-8 w-5 shrink-0 cursor-grab items-center justify-center text-muted-foreground/50 hover:text-muted-foreground active:cursor-grabbing"
        onPointerDown={(e) => dragControls.start(e)}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <Popover open={iconPickerOpen} onOpenChange={onIconPickerOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Escolher ícone"
            className={cx(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors',
              selectedIcon
                ? 'border-transparent bg-primary/10 text-primary hover:bg-primary/20'
                : 'border-dashed border-muted-foreground/40 text-muted-foreground hover:border-primary/40'
            )}
          >
            {selectedIcon ? (
              <selectedIcon.Icon className="h-4 w-4" aria-hidden="true" />
            ) : (
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="grid grid-cols-5 gap-1">
            {ONBOARDING_ICON_CATALOG.map((entry) => {
              const isSelected = iconKey === entry.key;

              return (
                <PopoverClose key={entry.key} asChild>
                  <button
                    type="button"
                    aria-label={entry.label}
                    className={cx(
                      'flex h-8 w-8 items-center justify-center rounded-full border transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-transparent text-muted-foreground hover:border-primary/40 hover:text-foreground'
                    )}
                    onClick={() =>
                      setValue(`tabs.${index}.iconKey`, entry.key, {
                        shouldValidate: true,
                      })
                    }
                  >
                    <entry.Icon className="h-4 w-4" />
                  </button>
                </PopoverClose>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      <div className="min-w-0 flex-1">
        <Input
          value={title}
          onChange={(e) =>
            setValue(`tabs.${index}.title`, e.target.value, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          placeholder="Nome da aba"
          aria-invalid={!!titleErrorMessage}
          className="h-7 border-none bg-transparent px-1 text-sm font-medium shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        {titleErrorMessage && (
          <p className="px-1 text-xs text-red-500">{titleErrorMessage}</p>
        )}
        {!isInternalTab && (
          <div className="relative">
            <Input
              value={path}
              onChange={(e) =>
                setValue(`tabs.${index}.path`, maskTabLink(e.target.value), {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              placeholder="sualoja.com.br/pagina"
              className="h-6 border-none bg-transparent px-1 pr-5 text-xs text-muted-foreground shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center">
              <AnimatePresence mode="wait" initial={false}>
                {isCheckingLink ? (
                  <motion.span
                    key="checking"
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.6, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  </motion.span>
                ) : path && linkCheck.status === 'valid' ? (
                  <motion.span
                    key="valid"
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.6, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                  >
                    <CheckCircle2 className="h-3 w-3 text-primary" />
                  </motion.span>
                ) : path && linkCheck.status === 'invalid' ? (
                  <motion.span
                    key="invalid"
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.6, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <AlertCircle className="h-3 w-3 text-amber-500" />
                  </motion.span>
                ) : null}
              </AnimatePresence>
            </div>
          </div>
        )}
        {!isInternalTab &&
          (pathErrorMessage ? (
            <p className="px-1 text-xs text-red-500">{pathErrorMessage}</p>
          ) : (
            !isCheckingLink &&
            path &&
            linkCheck.status === 'invalid' && (
              <p className="px-1 text-xs text-amber-600">{linkCheck.message}</p>
            )
          ))}
        {iconKeyErrorMessage && (
          <p className="px-1 text-xs text-red-500">{iconKeyErrorMessage}</p>
        )}
      </div>

      {isInternalTab && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex shrink-0 cursor-default items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              <Smartphone className="h-3 w-3" />
              App
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Esta aba abre uma tela que já existe no app — sem precisar de link.
          </TooltipContent>
        </Tooltip>
      )}

      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Remover aba"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
